import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AssetFilters } from 'app/models/control-center/asset.model';
import { PermissionType } from 'app/models/control-center/permission.model';
import { Utils } from 'app/utils';
import { AbstractControl, FormControl, FormGroup, NonNullableFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { HttpService } from 'app/services/http.service';
import { debounce, isEmpty, isEqual } from 'lodash-es';

interface BulkActionConfirmForm {
  numItems: FormControl<number>;
}

export interface TableActionPermissions {
    [key: string]: PermissionType;
}

@Component({
  selector: 'app-asset-table',
  templateUrl: './assetTable.component.html',
  styleUrls: ['./assetTable.component.scss'],
})
export class AssetTableComponent implements OnInit, AfterViewInit {

  constructor(
    private readonly httpService: HttpService,
    private readonly fb: NonNullableFormBuilder,
    private readonly dialog: MatDialog,
  ) { }

  @Output() createClick = new EventEmitter<any>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() deleteClick = new EventEmitter<any>();
  @Output() editClick = new EventEmitter<any>();
  @Output() importClick = new EventEmitter<any>();
  @Output() actionClick = new EventEmitter<any>();

  @Input() paginationUrl: string;
  @Input() filterOptionsUrl: string;
  @Input() paginationItemManipulation: (item: any) => any;
  @Input() displayedColumns: any = [];
  @Input() checkBoxes = false;
  @Input() search = true;
  @Input() uniqueKey = 'default';
  @Input() clickToEdit = true;
  @Input() showAdd = true;
  @Input() showDelete = true;
  @Input() paginate = true;
  @Input() permission = PermissionType.None;
  @Input() showImport = false;
  @Input() filters: any = [];
  @Input() additionalActions: Array<{ name: string, key: string, requireItemSelection: boolean }>;
  @Input() actionPermissions: TableActionPermissions = null;
  @Input() confirmItemCountOnBulkAction: boolean = true;

  /** Provide a function here to parse row data before sending it to the csv builder.  Useful when you need to wrap or tweak
   * values before exporting.
   */
  @Input() exportRowOverride?: (row: any) => void;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('deleteDialog') deleteDialog: any;
  // @ViewChild('deleteManyDialog') deleteManyDialog: any;
  @ViewChild('bulkActionDialog') bulkActionDialog!: TemplateRef<any>;

  bulkActionConfirmForm!: FormGroup<BulkActionConfirmForm>;

  selection = new SelectionModel<any>(true, []);

  fetchingData = true;
  filteredDataSource: any;
  columns: any = [];


  afterLoadDataSource: any = [];
  permissionType = PermissionType;

  appliedFilters: any = [];
  defaultFilterPredicate;
  previousMap: any;
  previousMapValues: any = {};
  filterDictionary = new Map<string, string[]>();

  private readonly filterOptionsCache = new Map<string, any[]>();
  private filterOptionsLoaded = false;

  private readonly debouncedFetchPaginationData = debounce(this.fetchPaginationData.bind(this), 300);

  ngOnInit(): void {
    this.filteredDataSource = new MatTableDataSource<any>();
    this.filteredDataSource.filterPredicate = () => true;
    this.defaultFilterPredicate = this.filteredDataSource.filterPredicate;

    if (this.checkBoxes && this.canPerformAtLeastOneAction()) this.columns.push('select');
    for (const column of this.displayedColumns) {
        this.columns.push(column.key);
    }

    this.initializeData();
  }

  private async initializeData() {
    await this.fetchFilterOptions();
    this.loadPresetFilters();
}

  private async fetchFilterOptions(): Promise<void> {
    if (!this.filterOptionsUrl || this.filterOptionsLoaded) return;

    try {
        const filtersToLoad = this.filters.filter(f => f.populateValues);
        if (filtersToLoad.length === 0) {
        this.filterOptionsLoaded = true;
        return;
        }

        const response = await this.httpService.post(this.filterOptionsUrl, {
            columnFilters: filtersToLoad.map(f => f.key),
        });

        for (const filter of filtersToLoad) {
            const rawOptions = response[filter.key] || [];
            const formattedOptions = rawOptions.map(option => ({
                value: String(option).replaceAll(' ', ''),
                label: option,
            }));
            this.filterOptionsCache.set(filter.key, formattedOptions);
        }

        this.filterOptionsLoaded = true;
    } catch (error) {
        console.error('Failed to load filter options:', error);
        this.filterOptionsLoaded = true;
    }
  }

  async fetchPaginationData() {
    this.fetchingData = true;
    const ogBody = this.generateSearchBody();
    const firstValue = await this.fetchUrlData(this.paginationUrl, ogBody);
    if (!this.validateSameQuery(ogBody)) return;
    this.addDataToSource(firstValue.data);
    let continuationId = firstValue.continuationId;
    while (continuationId) {
        if (!this.validateSameQuery(ogBody)) return;
        const nextPage = await this.fetchUrlData(this.paginationUrl, { ...ogBody, continuationId });
        if (!this.validateSameQuery(ogBody)) return;
        this.addDataToSource(nextPage.data);
        continuationId = nextPage.continuationId;
    }
    this.fetchingData = false;
  }

  async fetchUrlData(url, body) {
    const response = await this.httpService.post(url, body);
    return response;
  }

  validateSameQuery(input: AssetFilters): boolean {
    return isEqual(this.generateSearchBody(), input);
  }

  generateSearchBody(): AssetFilters {
    const body: AssetFilters = {
        limit: 1000,
    };
    for (const filter of this.filterDictionary) {
        const key = filter[0];
        const value = filter[1];
        const isDate = this.filters?.find(f => f.key === key)?.type === 'dateRange';
        if (isEmpty(value)) continue;

        if (isDate) {
            body[key + 'Start'] = value[0];
            body[key + 'End'] = value[1];
        } else body[key] = value;
    }
    return body;
  }

  addDataToSource(data) {
    if (this.paginationItemManipulation) {
        data = data.map(item => this.paginationItemManipulation(item));
    }
    this.filteredDataSource.data = [...this.filteredDataSource.data, ...data];
    for (const filter of this.filters) {
        if (filter.populateValues) {
            filter.options = this.getFilterOptions(filter.key, filter.options);
        }
    }
  }

getFilterOptions(columnName: string, currentOptions: any[]): any {
    const cachedOptions = this.filterOptionsCache.get(columnName) || [];
    const newValues = Array.from(new Set(this.filteredDataSource.data.map((item: any) => item[columnName])))
        .filter(value => value != null && value !== '' && String(value).trim() !== '')
        .sort();
    const newOptions = Object.keys(newValues).map(k => {
        return { value: (newValues[k] == null ? '' : newValues[k].replaceAll(' ', '')), label: newValues[k] };
    });

    // Merge current options with new options, removing duplicates based on value
    const allOptions = [...cachedOptions, ...(currentOptions || []), ...newOptions];
    const uniqueOptions = allOptions.filter((option, index, self) =>
        index === self.findIndex(opt => opt.value === option.value) &&
        option.value !== '' &&
        option.label != null &&
        String(option.label).trim() !== '',
    );

    return uniqueOptions.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
}

  ngAfterViewInit(): void {
    if (this.paginate) {
      this.filteredDataSource.paginator = this.paginator;

      this.paginator.page.subscribe(() => {
        this.selection.clear();
      });
    }
    this.filteredDataSource.sort = this.sort;
  }

  canPerformAtLeastOneAction(): boolean {
    if (!this.actionPermissions) return this.permission >= PermissionType.Edit;
    return Object.values(this.actionPermissions).some(permission => this.permission >= permission);
  }

  canPerformAction(type: string): boolean {
    if (!this.actionPermissions?.[type]) return this.permission >= PermissionType.Edit;
    return this.permission >= this.actionPermissions[type];
  }

  applySearch(event: Event) {
    const filterValue = ((event.target as HTMLInputElement).value).trim().replaceAll(/\s*,\s*/g, ',').toLowerCase();
    const filterName = (event.target as HTMLInputElement).name;

    this.updateFilteredDataSource(filterName, filterValue);
  }

  matchesCountValidator(expected: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const val = Number(control.value);
      return val === expected ? null : { mismatch: true };
    };
  }

  applyFilter(value: string, column: string) {
    const current = this.filterDictionary.get(column) ?? [];
    const index = current.indexOf(value);
    if (index === -1) {
        current.push(value);
    } else {
        current.splice(index, 1);
    }

    this.updateFilteredDataSource(column, current);
  }

  dateRangeChange(key: string, start: HTMLInputElement, end: HTMLInputElement) {
    const range = [];
    if (start.value && end.value) {
        const startDate = new Date(start.value).setHours(0, 0, 0, 0);
        const endDate = new Date(end.value).setHours(23, 59, 59, 999);

        range.push(startDate);
        range.push(endDate);
    }

    this.updateFilteredDataSource(key, range);
  }

  updateFilteredDataSource(key: string, value: any) {
    if (isEmpty(value) || !value) this.filterDictionary.delete(key);
    else this.filterDictionary.set(key, value);
    sessionStorage.setItem(`${this.uniqueKey}-map`, JSON.stringify(Object.fromEntries(this.filterDictionary)));
    this.fetchingData = true;
    this.filteredDataSource.data = [];
    this.debouncedFetchPaginationData();
    this.selection.clear();
  }

  loadPresetFilters() {
    this.previousMap = JSON.parse(sessionStorage.getItem(`${this.uniqueKey}-map`));
    if (this.previousMap) {
        Object.entries(this.previousMap).forEach(([key, value]) => {
            const filter = this.filters.find(f => f.key === key);
            if (filter || key === 'globalSearch') {
                if (Array.isArray(filter?.options) && Array.isArray(value) && value.length) {
                    const cachedOptions = this.filterOptionsCache.get(key) || [];
                    for (const v of value) {
                        const existsInCurrent = filter.options.find(o => o.label === v);
                        const existsInCached = cachedOptions.find(o => o.label === v);
                        if (!existsInCurrent && !existsInCached) {
                            filter.options.push({ value: (v as string).replaceAll(' ', ''), label: v });
                        }
                    }
                }
                if (filter?.type === 'dateRange' && Array.isArray(value) && value.length === 2) {
                    this.previousMapValues[key] = { start: new Date(value[0]), end: new Date(value[1]) };
                } else if (Array.isArray(value)) {
                    this.previousMapValues[key] = value.map(x => x.replaceAll(' ', ''));
                } else {
                    this.previousMapValues[key] = value;
                }
                this.updateFilteredDataSource(key, value);
            } else {
                console.log('Removing invalid filter from storage:', key);
                this.updateFilteredDataSource(key, null);
            }
        });
        this.debouncedFetchPaginationData();
    } else this.debouncedFetchPaginationData();
  }

  isAllSelected(): boolean {
    const visible = this.getVisibleRows();

    return visible.length > 0 && visible.length === this.selection.selected.length;
  }

  isAnySelected() {
    const numSelected = this.selection.selected.length;
    return numSelected > 0;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  private getVisibleRows(): any[] {
    const data = this.filteredDataSource.filteredData;
    if (this.paginator) {
      const start = this.paginator.pageIndex * this.paginator.pageSize;
      return data.slice(start, start + this.paginator.pageSize);
    }
    return data;
  }

  /** Selects visible rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.getVisibleRows());
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }

  create() {
    this.createClick.emit();
  }

  import() {
    this.importClick.emit();
  }

  async export() {
    const data = this.isAnySelected()
        ? this.filteredDataSource.filteredData.filter(row => this.selection.isSelected(row))
        : this.filteredDataSource.filteredData;

    const exportData = this.exportRowOverride
        ? data.map(row => {
            const rowCopy = { ...row };
            this.exportRowOverride(rowCopy);
            return rowCopy;
        })
        : data;

    Utils.downloadCsv(exportData);
  }

  onBulkActionConfirm(dialogRef: MatDialogRef<any>) {
    if (this.bulkActionConfirmForm.invalid) {
      return;
    }
    dialogRef.close('confirm');
  }

  deleteMany() {
    const count = this.selection.selected.length;
    this.bulkActionConfirmForm = this.fb.group({
      numItems: this.fb.control(null, [Validators.required, this.matchesCountValidator(count)]),
    });

    const dialogRef = this.dialog.open(this.bulkActionDialog, {
      data: {
        title: `Enter ${this.selection.selected.length} to confirm deletion of ${this.selection.selected.length} items`,
        confirmButtonText: 'Delete',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        const ids = this.selection.selected.map(s => s.id);
        this.deleteClick.emit(ids);
      }
    });
  }

  handleActionClick(key): void {
    if (this.confirmItemCountOnBulkAction) {
      const count = this.selection.selected.length;
      this.bulkActionConfirmForm = this.fb.group({
        numItems: this.fb.control(null, [Validators.required, this.matchesCountValidator(count)]),
      });
      const dialogRef = this.dialog.open(this.bulkActionDialog, {
        data: {
          title: `Enter ${this.selection.selected.length} to continue with bulk-updating ${this.selection.selected.length} items`,
          confirmButtonText: 'Delete',
        },
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result === 'confirm') {
          const ids = this.selection.selected.map(s => s.id);
          this.actionClick.emit({ key, ids });
        }
      });
    } else {
      const ids = this.selection.selected.map(s => s.id);
      this.actionClick.emit({ key, ids });
    }
  }

  delete(id: any) {
    const dialogRef = this.dialog.open(this.deleteDialog);
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'yes') {
        this.deleteClick.emit(id);
      }
    });
  }

  click(id: string, route: boolean) {
    if (route) this.rowClick.emit(id);
  }
}
